import { reportSyncPause, markSyncKeyVerified } from '../sync/sync-pause.js';
import nacl from 'tweetnacl';
import { Keypair } from './keypair.js';
import { VaultStorage } from './vault-storage.js';
import { getCurrentUser } from '../auth/handlers.js';
import { createAuthorityEvent, enrollmentStart, followAuthority } from './key-authority.js';
import { IdentityPins } from './identity-pins.js';
import { encryptInviteTitle } from './invite-title.js';
import { newLinkSecret, linkKeys, encryptLinkSecret } from './link-keys.js';
import { t } from '../../shared/i18n/index.js';

const headers = { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
const stopped = () => new Error(t('account.keyTrust.stopped'));
const equal = (a, b) => a.length === b.length && nacl.verify(a, b);
const restorePending = () => {
  const error = new Error(t('account.keyTrust.restorePending'));
  error.code = 'access_restore_pending';
  return error;
};
async function canOpen(wrapped, secretKey) {
  try {
    (await Keypair.unwrapDEKAnonymous(Keypair.fromBase64(wrapped), secretKey)).fill(0);
    return true;
  } catch {
    return false;
  }
}
const ownerKeyReplaced = () => {
  const error = new Error(t('account.keyTrust.ownerKeyReplaced'));
  error.code = 'owner_key_replaced';
  return error;
};
const ownerKey = state => state.members.find(m => m.role === 'owner')?.publicKey;

const fingerprint = key => Keypair.toBase64(nacl.hash(Keypair.fromBase64(key))).slice(0, 12);

/**
 * Vault-level identity pins (A2 / S-M3), loaded for the signed-in account and
 * seeded from every map's verified authority state.
 */
function pins() {
  const self = getCurrentUser()?.user_id || null;
  IdentityPins.load(self);
  IdentityPins.seed((VaultStorage.getIndex()?.docs || []).filter(d => d.keyAuthority), self);
  return IdentityPins;
}

const describeKey = key => (key ? fingerprint(key) : t('account.keyTrust.unknownKey'));

/** A collaborator's key differs from the one this vault pinned for them. */
function identityChanged(identity, pin, name) {
  const who = identity.username || name || t('account.keyTrust.thisUser');
  const error = new Error(pin.status === 'conflict'
    ? t('account.keyTrust.identityConflict', {
      name: who, fingerprint: fingerprint(identity.publicKey), keys: pin.seenKeys.map(describeKey).join(', '),
    })
    : t('account.keyTrust.identityChanged', {
      name: who, fingerprint: fingerprint(identity.publicKey), previous: describeKey(pin.pinnedKey),
    }));
  error.code = 'identity_changed';
  // The placeholder name is for display only; never store it in a pin.
  error.identity = { ...identity, username: identity.username || name || null, pinnedKey: pin.pinnedKey, seenKeys: pin.seenKeys, status: pin.status };
  return error;
}

// S-H1: events that failed verification and were skipped, per map, from the
// last read. The owner's next sharing change supersedes them.
const unverified = new Map();
// The latest verified state per map from the last read, which may be ahead
// of the vault's copy (a rekey doesn't change our wrapped key, so it isn't
// written back until the next key change).
const lastVerified = new Map();

/**
 * Sharing changes of this map that couldn't be verified and were ignored
 * (the last verified state stays in force).
 * @returns {Array<{id: string, epoch: number, kind: string, actorId: string}>}
 */
export function getUnverifiedEvents(docId) {
  return unverified.get(docId)?.skipped || [];
}

/**
 * Walk a map's authority history to find unverifiable changes, for display
 * only: failures are swallowed and never pause sync, nothing is written.
 * @returns {Promise<Array>} see getUnverifiedEvents
 */
export async function inspectAuthority(docId) {
  try {
    (await read(docId))?.dek?.fill(0);
  } catch { /* shown when a sharing change needs it */ }
  return getUnverifiedEvents(docId);
}

/**
 * Members of a map whose key in its verified state disagrees with the vault's
 * pins (a key changed since it was first seen, or maps disagree).
 * @returns {Array<{userId, username, publicKey, pinnedKey, status, previousKey}>}
 */
export function getPinWarnings(docId) {
  const stored = VaultStorage.getDoc(docId)?.keyAuthority;
  const read = lastVerified.get(docId);
  const state = read && (!stored || read.epoch >= stored.epoch) ? read : stored;
  if (!state) return [];
  return pins().mismatchesIn(state, getCurrentUser()?.user_id || null);
}

/**
 * The user compared `publicKey` with its owner out of band and it matched:
 * pin it for this vault (the previous pin is kept as history).
 */
export function confirmIdentity(userId, publicKey, username) {
  pins().accept(userId, publicKey, username);
}

/** Record the identities of a map the user just joined (after they confirmed). */
export function rememberIdentities(docId, state) {
  if (state) pins().observeState(state, { docId, self: getCurrentUser()?.user_id || null });
}

/**
 * The public key the server publishes for us must be the one whose secret is
 * in our vault. Accounts that generated keys on several devices before the
 * server refused replacements (2026-09-08) can have diverged silently: every
 * package addressed to them is then unreadable, and any map they own fails
 * verification for all recipients. Check explicitly so the user gets a precise
 * error instead of a half-finished share.
 */
export async function assertOwnIdentityRegistered() {
  const stored = await VaultStorage.getKeypair();
  if (!stored) throw stopped();
  const response = await fetch('/api/keypair', { credentials: 'include' });
  if (response.status === 404) return; // Not published yet: KeypairManager uploads it.
  if (!response.ok) throw stopped();
  const published = (await response.json()).public_key;
  if (published !== stored.publicKey) {
    const error = new Error(t('account.keyTrust.ownMismatch', {
      device: fingerprint(stored.publicKey), published: fingerprint(published),
    }));
    error.code = 'identity_mismatch';
    throw error;
  }
}

async function identity() {
  const stored = await VaultStorage.getKeypair();
  const user = getCurrentUser();
  if (!stored || !user?.user_id) throw stopped();
  return { userId: user.user_id, keypair: {
    publicKey: Keypair.fromBase64(stored.publicKey), secretKey: Keypair.fromBase64(stored.secretKey),
  } };
}

/**
 * Resolve a username or email to the account's identity. The authority
 * roster, which every member of a map sees, names the account by the
 * server's canonical username — never by the typed identifier, which may be
 * an email (S-M2). The server returns the username only for a username
 * lookup (an email lookup must not reveal it); otherwise the roster carries
 * no name for that member.
 */
export async function lookupIdentity(identifier) {
  const response = await fetch(`/api/users/${encodeURIComponent(identifier)}/pubkey`, { credentials: 'include' });
  if (!response.ok) throw new Error(t('account.keyTrust.recipientLoadFailed'));
  const data = await response.json();
  if (!data.user_id || Keypair.fromBase64(data.public_key).length !== 32) throw stopped();
  return { userId: data.user_id, publicKey: data.public_key,
    ...(typeof data.username === 'string' && data.username && { username: data.username }) };
}

async function history(docId, fromEpoch = 0) {
  const events = [];
  let after = fromEpoch - 1;
  for (;;) {
    const response = await fetch(`/api/shares/${docId}/key-authority?after_epoch=${after}`, { credentials: 'include' });
    if (response.status === 404 && !events.length) return [];
    if (!response.ok) throw stopped();
    const page = await response.json();
    if (!Array.isArray(page) || page.length > 100) throw stopped();
    for (const event of page) {
      if (!Number.isSafeInteger(event.state?.epoch) || event.state.epoch <= after) throw stopped();
      after = event.state.epoch;
      events.push(event);
    }
    if (page.length < 100) return events;
  }
}

async function read(docId, { enrollment = false, candidate = null, ownerUsername = null } = {}) {
  const local = VaultStorage.getDoc(docId);
  if (local?._keyAuthorityConflict) throw stopped();
  let events = await history(docId, local?.keyAuthority?.epoch || 0);
  if (!events.length) {
    if (local?.keyAuthority) throw stopped();
    return null;
  }
  const { userId, keypair } = await identity();
  const ownKey = Keypair.toBase64(keypair.publicKey);
  let state = local?.keyAuthority || null;
  let dek = null;
  let pinnedOwner = state && ownerKey(state);
  const localIsOwner = local && (!local.role || local.role === 'owner');
  if (localIsOwner) {
    if (state && ownerKey(state) !== ownKey) { keypair.secretKey.fill(0); throw ownerKeyReplaced(); }
    pinnedOwner = ownKey;
  }
  // Our identity key was replaced (identity repair) after this map pinned the
  // old one: packages sealed to it can't be opened. Re-enroll from the owner's
  // rekey naming us, still anchored on the owner key we pinned.
  let reenroll = !!state && !localIsOwner &&
    state.witnesses?.find(w => w.userId === userId)?.publicKey !== ownKey;
  // Same when nothing was pinned yet but our stored map key is sealed to the
  // replaced identity: it can no longer anchor continuity, the owner's rekey must.
  if (!state && !enrollment && !localIsOwner && local?.wrappedDEK &&
      !(await canOpen(local.wrappedDEK, keypair.secretKey))) reenroll = true;
  if (reenroll) state = null;
  if (!state) {
    // Start at the first package addressed to our current key.
    const start = enrollmentStart(events, userId, ownKey, { requireOwnerRekey: reenroll });
    if (start < 0) { keypair.secretKey.fill(0); throw restorePending(); }
    events = events.slice(start);
  }
  if (!pinnedOwner) {
    // Initial enrollment only: trust the owner identity via the existing TLS
    // directory, then retain it in the encrypted vault. Never repeat for a pin.
    // Before accepting, the invitation itself names the owner.
    if (!ownerUsername) {
      const response = await fetch(`/api/shares/${docId}`, { credentials: 'include' });
      if (!response.ok) throw stopped();
      ownerUsername = (await response.json()).owner_username;
    }
    const owner = await lookupIdentity(ownerUsername);
    pinnedOwner = owner.publicKey;
    const chainOwner = ownerKey(events[0].state || { members: [] });
    if (!state && chainOwner && chainOwner !== pinnedOwner) {
      const error = new Error(t('account.keyTrust.ownerMismatch', {
        owner: ownerUsername, published: fingerprint(pinnedOwner), chain: fingerprint(chainOwner),
      }));
      error.code = 'owner_identity_mismatch';
      keypair.secretKey.fill(0);
      throw error;
    }
  }
  let anchor = null;
  let anchorMatched = !!state || enrollment || reenroll;
  let enrolledBy = null;
  let skipped = [];
  let head = null;
  try {
    if (state && local?.wrappedDEK) {
      dek = await Keypair.unwrapDEKAnonymous(Keypair.fromBase64(local.wrappedDEK), keypair.secretKey);
    }
    if (!anchorMatched && local?.wrappedDEK) {
      anchor = await Keypair.unwrapDEKAnonymous(Keypair.fromBase64(local.wrappedDEK), keypair.secretKey);
    }
    if (state && !events.some(e => e.state?.id === state.id)) throw stopped();
    const enrolling = !state;
    const walked = followAuthority(events, {
      docId, userId, keypair, state, dek, ownerPublicKey: pinnedOwner, enrollment: enrollment || reenroll,
      onVerified: result => {
        if (anchor && result.dek && equal(anchor, result.dek)) anchorMatched = true;
        // S-L1: who admitted us — the actor of the first event we could open.
        if (enrolling && !enrolledBy) enrolledBy = result.state.members.find(m => m.userId === result.state.actorId) || null;
      },
    });
    ({ state, dek, skipped, head } = walked);
    if (!anchorMatched) throw stopped();
    if (!dek && local?.wrappedDEK && !reenroll) {
      dek = await Keypair.unwrapDEKAnonymous(Keypair.fromBase64(local.wrappedDEK), keypair.secretKey);
    }
    if (!state || !dek || !state.members.some(m => m.userId === userId)) throw stopped();
    if (candidate) {
      const raw = await Keypair.unwrapDEKAnonymous(Keypair.fromBase64(candidate), keypair.secretKey);
      const matches = equal(raw, dek); raw.fill(0);
      if (!matches) throw stopped();
    }
    if (skipped.length) {
      console.warn(`[MapKeyTrust] ${skipped.length} sharing change(s) of map ${docId} could not be verified and were ignored`);
      unverified.set(docId, { skipped, head, base: state.id });
    } else {
      unverified.delete(docId);
    }
    // A joined map's identities are pinned once the user accepts; before
    // that, a declined forged invitation must not pin anything.
    if (!enrollment) {
      pins().observeState(state, { docId, self: userId });
      lastVerified.set(docId, state);
    }
    return { state, dek, skipped, head, enrolledBy };
  } catch (error) {
    dek?.fill(0);
    throw error;
  } finally { keypair.secretKey.fill(0); anchor?.fill(0); }
}

/** Existing keys may be rewrapped; a different DEK requires authority proof. */
export async function verifyReplacement(docId, wrapped) {
  try {
    const authority = await verifyReplacementImpl(docId, wrapped);
    markSyncKeyVerified(docId);
    return authority;
  } catch (error) {
    reportSyncPause('map', docId, VaultStorage.getDoc(docId)?._keyAuthorityConflict ? 'conflict' : 'key');
    throw error;
  }
}

async function verifyReplacementImpl(docId, wrapped) {
  const local = VaultStorage.getDoc(docId);
  if (local?._keyAuthorityConflict) throw stopped();
  if (!local?.wrappedDEK || local.wrappedDEK === wrapped) return local?.keyAuthority || null;
  const stored = await VaultStorage.getKeypair();
  if (!stored) throw stopped();
  const secret = Keypair.fromBase64(stored.secretKey);
  let previous, replacement;
  try {
    // The stored wrap may be sealed to our replaced identity key (identity
    // repair); then only the authority chain can vouch for the new one.
    try {
      previous = await Keypair.unwrapDEKAnonymous(Keypair.fromBase64(local.wrappedDEK), secret);
    } catch {
      previous = null;
    }
    replacement = await Keypair.unwrapDEKAnonymous(Keypair.fromBase64(wrapped), secret);
    if (previous && equal(previous, replacement)) return local.keyAuthority || null;
  } finally { secret.fill(0); previous?.fill(0); replacement?.fill(0); }
  const verified = await read(docId, { candidate: wrapped });
  if (!verified) throw stopped();
  verified.dek.fill(0);
  return verified.state;
}

export async function enroll(docId, wrapped, { ownerUsername = null } = {}) {
  return (await inspectEnrollment(docId, wrapped, { ownerUsername })).state;
}

/**
 * Enrollment with what the user must see before joining (S-L1, S-L5): the
 * owner and the member who admitted us, with their keys, and every one of
 * them whose key disagrees with the vault's pins. `state` is null for a map
 * with no authority history: its key can't be verified at all.
 * @returns {Promise<{state: Object|null, owner: Object|null, inviter: Object|null, mismatches: Array}>}
 */
export async function inspectEnrollment(docId, wrapped, { ownerUsername = null } = {}) {
  const verified = await read(docId, { enrollment: true, candidate: wrapped, ownerUsername });
  if (!verified) return { state: null, owner: null, inviter: null, mismatches: [] }; // Legacy map: first-enrollment trust only.
  verified.dek.fill(0);
  const { state, enrolledBy } = verified;
  const owner = state.members.find(m => m.role === 'owner') || null;
  const inviter = enrolledBy || owner;
  const current = pins();
  const mismatches = [owner, inviter]
    .filter((m, i, all) => m && all.findIndex(x => x?.userId === m.userId) === i)
    .map(m => ({ ...m, ...current.check(m.userId, m.publicKey) }))
    .filter(m => m.status === 'changed' || m.status === 'conflict');
  return { state, owner, inviter, mismatches };
}

/**
 * Verify a pending invitation before accepting it: the same checks as
 * enrollment, run while nothing has been committed yet. Returns the verified
 * authority state (null for maps without authority history). Throws with a
 * specific message when our own identity or the owner's doesn't check out.
 */
export async function verifyInvitation(docId, wrapped, ownerUsername) {
  return (await inspectInvitation(docId, wrapped, ownerUsername)).state;
}

/** `verifyInvitation` with the details of `inspectEnrollment`. */
export async function inspectInvitation(docId, wrapped, ownerUsername) {
  await assertOwnIdentityRegistered();
  return inspectEnrollment(docId, wrapped, { ownerUsername });
}

export async function synchronize(docId) {
  try {
    const state = await synchronizeImpl(docId);
    if (state) markSyncKeyVerified(docId);
    return state;
  } catch (error) {
    reportSyncPause('map', docId, VaultStorage.getDoc(docId)?._keyAuthorityConflict ? 'conflict' : 'key');
    throw error;
  }
}

async function synchronizeImpl(docId) {
  const verified = await read(docId);
  if (!verified) return null;
  try {
    const response = await fetch(`/api/shares/${docId}/my-dek`, { credentials: 'include' });
    if (!response.ok) throw stopped();
    const candidate = await response.json();
    if (candidate.is_revoked) throw stopped();
    const stored = await VaultStorage.getKeypair();
    const secret = Keypair.fromBase64(stored.secretKey);
    let raw;
    try {
      raw = await Keypair.unwrapDEKAnonymous(Keypair.fromBase64(candidate.wrapped_dek), secret);
      if (!equal(raw, verified.dek)) throw stopped();
    } finally { secret.fill(0); raw?.fill(0); }
    const local = VaultStorage.getDoc(docId);
    if (local?.wrappedDEK !== candidate.wrapped_dek || local?.keyAuthority?.id !== verified.state.id) {
      await VaultStorage.updateDocWrappedDEK(docId, candidate.wrapped_dek, { verifiedAuthority: verified.state });
    }
    return verified.state;
  } finally { verified.dek.fill(0); }
}

const initializing = new Map();
export async function ensure(docId) {
  if (initializing.has(docId)) return initializing.get(docId);
  const pending = ensureOnce(docId);
  initializing.set(docId, pending);
  try { return await pending; } finally { initializing.delete(docId); }
}

async function ensureOnce(docId) {
  const current = await synchronize(docId);
  if (current) return (await supersede(docId, current)) || current;
  const local = VaultStorage.getDoc(docId);
  if (!local || (local.role && local.role !== 'owner')) throw stopped();
  const { userId, keypair } = await identity();
  const dek = await VaultStorage.getDocDEK(docId);
  try {
    const response = await fetch(`/api/shares/${docId}/permissions`, { credentials: 'include' });
    if (!response.ok) throw stopped();
    const permissions = await response.json();
    const members = [];
    // The first authority of a legacy map trusts the server's keys; at least
    // they must agree with the keys this vault pinned (A2).
    const current = pins();
    const checked = member => {
      const pin = current.check(member.userId, member.publicKey);
      if (pin.status === 'changed' || pin.status === 'conflict') throw identityChanged(member, pin);
      return member;
    };
    for (const permission of permissions) {
      const member = permission.user_id === userId
        ? { userId, publicKey: Keypair.toBase64(keypair.publicKey), username: permission.username }
        : checked(await lookupIdentity(permission.username));
      if (member.userId !== permission.user_id) throw stopped();
      members.push({ ...member, role: permission.role });
    }
    // Preserve pending invitees through future rotations as well.
    const invitations = await fetch(`/api/shares/${docId}/invitations`, { credentials: 'include' });
    if (!invitations.ok) throw stopped();
    for (const invitation of await invitations.json()) {
      const member = checked(await lookupIdentity(invitation.invitee_username));
      if (!members.some(m => m.userId === member.userId)) members.push({ ...member, role: invitation.role, pending: true });
    }
    const event = createAuthorityEvent({ docId, ownerId: userId, actorId: userId, kind: 'bootstrap', members, dek, keypair });
    const saved = await fetch(`/api/shares/${docId}/key-authority`, { method: 'POST', credentials: 'include', headers, body: JSON.stringify(event) });
    if (!saved.ok) {
      if (saved.status === 409) {
        const concurrent = await synchronize(docId);
        if (concurrent) return concurrent;
      }
      throw stopped();
    }
    await VaultStorage.updateDocWrappedDEK(docId, local.wrappedDEK, { verifiedAuthority: event.state });
    return event.state;
  } finally { dek.fill(0); keypair.secretKey.fill(0); }
}

/**
 * Owner, after sharing changes by other members it could not verify (S-H1):
 * re-anchor the map's authority on the last state it verified. The roster is
 * that state limited to who still has access, plus non-admin members who
 * joined through the unverified events, under their published key when it
 * agrees with the vault's pins. The map key doesn't change.
 * @returns {Promise<Object|null>} the new state, or null when nothing to supersede
 */
async function supersede(docId, state) {
  const pending = unverified.get(docId);
  const self = getCurrentUser()?.user_id;
  if (!pending?.skipped.length || pending.base !== state.id || state.ownerId !== self) return null;
  const [permissionsResponse, invitationsResponse, linkResponse] = await Promise.all([
    fetch(`/api/shares/${docId}/permissions`, { credentials: 'include' }),
    fetch(`/api/shares/${docId}/invitations`, { credentials: 'include' }),
    fetch(`/api/shares/${docId}/link`, { credentials: 'include' }),
  ]);
  if (!permissionsResponse.ok || !invitationsResponse.ok || (!linkResponse.ok && linkResponse.status !== 404)) throw stopped();
  const access = new Map();
  for (const p of await permissionsResponse.json()) access.set(p.user_id, { role: p.role, username: p.username });
  for (const i of await invitationsResponse.json()) access.set(i.invitee_id, { role: i.role, username: i.invitee_username, pending: true });
  // The map's link keeps its entry (docs/plans/2026-10-28-link-sharing.md).
  const link = linkResponse.ok ? await linkResponse.json() : null;
  if (link) access.set(link.id, { role: link.role, link: true, publicKey: link.public_key });
  const members = state.members.filter(m => m.userId === state.ownerId || access.has(m.userId));
  const witnessed = new Map(state.witnesses.map(w => [w.userId, w.publicKey]));
  const current = pins();
  for (const [userId, entry] of access) {
    if (members.some(m => m.userId === userId) || ['owner', 'admin'].includes(entry.role)) continue;
    if (entry.link) {
      if (!witnessed.has(userId)) members.push({ userId, role: entry.role, publicKey: entry.publicKey, link: true });
      continue;
    }
    if (!entry.username) continue;
    let identity;
    try { identity = await lookupIdentity(entry.username); } catch { continue; }
    const pin = current.check(identity.userId, identity.publicKey);
    if (identity.userId !== userId || (witnessed.has(userId) && witnessed.get(userId) !== identity.publicKey) ||
        pin.status === 'changed' || pin.status === 'conflict') continue;
    members.push({ ...identity, role: entry.role, ...(entry.pending && { pending: true }) });
  }
  const { userId, keypair } = await identity();
  const dek = await VaultStorage.getDocDEK(docId);
  try {
    const event = createAuthorityEvent({ docId, previous: state, members, ownerId: state.ownerId, actorId: userId,
      kind: 'supersede', dek, keypair, chain: pending.head });
    const saved = await fetch(`/api/shares/${docId}/key-authority`, {
      method: 'POST', credentials: 'include', headers, body: JSON.stringify(event) });
    if (!saved.ok) {
      const body = await saved.json().catch(() => ({}));
      throw new Error(t('account.keyTrust.supersedeFailed', { error: body.error || String(saved.status) }));
    }
    await VaultStorage.updateDocWrappedDEK(docId, VaultStorage.getDoc(docId).wrappedDEK, { verifiedAuthority: event.state });
    unverified.delete(docId);
    console.warn(`[MapKeyTrust] Superseded ${pending.skipped.length} unverifiable sharing change(s) of map ${docId}`);
    return event.state;
  } finally { dek.fill(0); keypair.secretKey.fill(0); }
}

export async function prepare(docId, kind, members, dek, previous = null) {
  await assertOwnIdentityRegistered();
  previous ||= await ensure(docId);
  if (unverified.get(docId)?.base === previous.id) {
    // The server's head is past our last verified state: the change would be
    // refused. Only the owner can re-anchor (done by ensure()).
    const error = new Error(t('account.keyTrust.unverified'));
    error.code = 'authority_unverified';
    throw error;
  }
  const { userId, keypair } = await identity();
  try {
    return createAuthorityEvent({ docId, previous, members, ownerId: previous.ownerId, actorId: userId, kind, dek, keypair });
  } finally { keypair.secretKey.fill(0); }
}

/**
 * Authorize an invitation. If the invitee's published key disagrees with the
 * key this vault pinned for them, this throws `identity_changed` (with both
 * keys on `error.identity`) unless the caller passes `confirmedKey`: the new
 * key, after the user compared its fingerprint with the invitee out of band.
 * `commit()` records the identity once the server accepted the invitation.
 * @param {Object} [options]
 * @param {string} [options.confirmedKey]
 * @param {string} [options.title] - the map title, encrypted for the invitee (S-M1)
 */
export async function prepareInvite(docId, username, role, { confirmedKey = null, title = null } = {}) {
  // Recipients pin our published key; if it isn't the vault key we sign with,
  // they can never accept. Fail here, where the owner can see why.
  await assertOwnIdentityRegistered();
  const state = await ensure(docId);
  const identity = await lookupIdentity(username);
  const previous = state.members.find(m => m.userId === identity.userId);
  if (previous && previous.publicKey !== identity.publicKey) throw stopped();
  const pin = pins().check(identity.userId, identity.publicKey);
  const confirmed = confirmedKey === identity.publicKey;
  if ((pin.status === 'changed' || pin.status === 'conflict') && !confirmed) throw identityChanged(identity, pin, username);
  const members = state.members.filter(m => m.userId !== identity.userId);
  members.push({ ...identity, role, pending: true });
  const dek = await VaultStorage.getDocDEK(docId);
  try {
    const event = await prepare(docId, 'invite', members, dek, state);
    const wrappedDEK = Keypair.toBase64(await Keypair.wrapDEKAnonymous(dek, Keypair.fromBase64(identity.publicKey)));
    const encryptedTitle = title ? await encryptInviteTitle(dek, docId, title) : null;
    const self = getCurrentUser()?.user_id || null;
    const commit = () => confirmed
      ? IdentityPins.accept(identity.userId, identity.publicKey, identity.username)
      : IdentityPins.observe([identity], { docId, self });
    return { identity, event, wrappedDEK, encryptedTitle, commit };
  }
  finally { dek.fill(0); }
}

/**
 * The owner: a link to the map (docs/plans/2026-10-28-link-sharing.md; only
 * the owner shares by link, as only the owner removes people).
 * Its entry in the key log is sent as an `invite` (apps from before links
 * verify it as such); the server keeps the token and, under the map key, the
 * secret and the title. The secret is returned for the caller to show the
 * link once the server accepted it; the caller zeroes it.
 * @returns {Promise<{linkId: string, secret: Uint8Array, creatorPublicKey: string, event: Object, body: Object}>}
 */
export async function prepareLink(docId, role) {
  if (!['viewer', 'editor'].includes(role)) throw stopped();
  await assertOwnIdentityRegistered();
  const state = await ensure(docId);
  if (state.ownerId !== getCurrentUser()?.user_id) throw stopped();
  const secret = newLinkSecret();
  const { keypair, token } = await linkKeys(secret);
  keypair.secretKey.fill(0);
  const linkId = crypto.randomUUID();
  const entry = { userId: linkId, role, publicKey: Keypair.toBase64(keypair.publicKey), link: true };
  const dek = await VaultStorage.getDocDEK(docId);
  try {
    const event = await prepare(docId, 'invite', [...state.members, entry], dek, state);
    const title = VaultStorage.getDoc(docId)?.title;
    const body = {
      id: linkId, role, public_key: entry.publicKey, token: Keypair.toBase64(token),
      encrypted_secret: await encryptLinkSecret(dek, docId, linkId, secret),
      encrypted_title: title ? await encryptInviteTitle(dek, docId, title) : null,
      key_authority: event,
    };
    const self = state.members.find(m => m.userId === event.state.actorId);
    return { linkId, secret, creatorPublicKey: self.publicKey, event, body };
  } catch (error) {
    secret.fill(0);
    throw error;
  } finally { dek.fill(0); token.fill(0); }
}

/** The owner: the role the link gives (whoever has the map open by it gets it at once). */
export async function prepareLinkRole(docId, linkId, role) {
  if (!['viewer', 'editor'].includes(role)) throw stopped();
  const state = await ensure(docId);
  if (!state.members.some(m => m.userId === linkId && m.link === true)) throw stopped();
  const members = state.members.map(m => (m.userId === linkId ? { ...m, role } : m));
  const dek = await VaultStorage.getDocDEK(docId);
  try { return await prepare(docId, 'role', members, dek, state); } finally { dek.fill(0); }
}

/**
 * Whoever holds a link: the map's key log walked as the link, from its entry
 * on. That entry must come from the owner, whose key the link names: not
 * what the server says, which could otherwise present another map, or a
 * member list of its own, under the link.
 * @returns {{state: Object, dek: Uint8Array, role: string}} the caller zeroes `dek`
 */
export function verifyLinkChain(docId, linkId, keypair, ownerPublicKey, events) {
  const start = enrollmentStart(events, linkId, Keypair.toBase64(keypair.publicKey));
  if (start < 0) throw stopped();
  const first = events[start].state;
  const creator = first?.members?.find(m => m.userId === first.actorId);
  const entry = first?.members?.find(m => m.userId === linkId);
  if (first?.kind !== 'invite' || !creator || creator.role !== 'owner' || creator.publicKey !== ownerPublicKey ||
      first.actorId !== first.ownerId || entry?.link !== true) throw stopped();
  const walked = followAuthority(events.slice(start), { docId, userId: linkId, keypair, ownerPublicKey, enrollment: true });
  return linkWalked(walked, linkId);
}

/**
 * The key log's changes since `state` (verified before, by verifyLinkChain
 * or this), walked as the link: a key change gives the new map key. `dek` is
 * the current map key (copied, not changed).
 * @returns {{state: Object, dek: Uint8Array|null, role: string}} `dek` null
 *   when the map key did not change; the caller zeroes it
 */
export function followLinkChain(docId, linkId, keypair, ownerPublicKey, state, dek, events) {
  const current = dek.slice();
  const walked = followAuthority(events, { docId, userId: linkId, keypair, ownerPublicKey, state, dek: current });
  const result = linkWalked(walked, linkId);
  if (result.dek && nacl.verify(result.dek, dek)) {
    result.dek.fill(0);
    result.dek = null;
  }
  return result;
}

function linkWalked(walked, linkId) {
  const link = walked.state?.members.find(m => m.userId === linkId && m.link === true);
  if (!walked.dek || !link) {
    walked.dek?.fill(0);
    throw stopped();
  }
  return { state: walked.state, dek: walked.dek, role: link.role };
}

/**
 * Owner: what restoring a member's access would do — the key this map pinned
 * for them, the key the server publishes now, and how the new key compares
 * with the vault's pins. Nothing is changed.
 * @returns {Promise<{userId, username, previousKey, newKey, unchanged: boolean, pin: Object}>}
 */
export async function describeMemberRestore(docId, targetUserId, username) {
  await assertOwnIdentityRegistered();
  const state = await ensure(docId);
  if (state.ownerId !== getCurrentUser()?.user_id) throw stopped();
  const member = state.members.find(m => m.userId === targetUserId);
  if (!member || state.ownerId === targetUserId) throw stopped();
  const current = await lookupIdentity(username);
  if (current.userId !== targetUserId) throw stopped();
  return { userId: targetUserId, username: current.username || username, previousKey: member.publicKey,
    newKey: current.publicKey, unchanged: member.publicKey === current.publicKey,
    pin: pins().check(targetUserId, current.publicKey) };
}

/**
 * Owner: restore a member's (or pending invitee's) access after they repaired
 * their encryption identity. Re-wraps the map key for their current published
 * key and signs a `rekey` naming them; the server stores both atomically.
 *
 * A2: the server alone says the key changed and which key is new, so the
 * owner must first compare the new fingerprint with the member out of band
 * (see describeMemberRestore) and pass that key as `confirmedKey`. Anything
 * else — no confirmation, or a key that changed again meanwhile — is refused.
 * @param {string} docId
 * @param {string} targetUserId
 * @param {string} username - the member's username (from the permissions list)
 * @param {{confirmedKey?: string}} [options]
 * @returns {Promise<{previousKey: string, newKey: string}>}
 */
export async function restoreMemberAccess(docId, targetUserId, username, { confirmedKey = null } = {}) {
  if (!confirmedKey) {
    const error = new Error(t('account.keyTrust.verifyFirst'));
    error.code = 'verification_required';
    throw error;
  }
  await assertOwnIdentityRegistered();
  const state = await ensure(docId);
  const member = state.members.find(m => m.userId === targetUserId);
  if (!member || state.ownerId === targetUserId) throw stopped();
  const current = await lookupIdentity(username);
  if (current.userId !== targetUserId) throw stopped();
  if (current.publicKey !== confirmedKey) {
    const error = new Error(t('account.keyTrust.changedAgain', { name: username, fingerprint: fingerprint(current.publicKey) }));
    error.code = 'verification_required';
    throw error;
  }
  const { userId, keypair } = await identity();
  const dek = await VaultStorage.getDocDEK(docId);
  try {
    if (state.ownerId !== userId) throw stopped();
    const members = state.members.map(m => m.userId === targetUserId ? { ...m, publicKey: current.publicKey } : m);
    const event = createAuthorityEvent({ docId, previous: state, members, ownerId: state.ownerId,
      actorId: userId, kind: 'rekey', target: targetUserId, dek, keypair });
    const wrappedDEK = Keypair.toBase64(await Keypair.wrapDEKAnonymous(dek, Keypair.fromBase64(current.publicKey)));
    const response = await fetch(`/api/shares/${docId}/members/${targetUserId}/restore-key`, {
      method: 'POST', credentials: 'include', headers,
      body: JSON.stringify({ wrapped_dek: wrappedDEK, key_authority: event }),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error || t('account.keyTrust.restoreFailed'));
    }
    await VaultStorage.updateDocWrappedDEK(docId, VaultStorage.getDoc(docId).wrappedDEK, { verifiedAuthority: event.state });
    IdentityPins.accept(targetUserId, current.publicKey, current.username || username);
    return { previousKey: member.publicKey, newKey: current.publicKey };
  } finally { dek.fill(0); keypair.secretKey.fill(0); }
}

/**
 * After an identity repair (and on sign-in): shared maps whose stored key
 * can't be opened with the current identity are re-verified. Those an owner
 * already restored get the new key; the rest wait for their owner.
 * @returns {Promise<{restored: string[], pending: string[]}>}
 */
export async function refreshRestoredShares() {
  const result = { restored: [], pending: [] };
  const stored = await VaultStorage.getKeypair();
  if (!stored) return result;
  const docs = (VaultStorage.getIndex()?.docs || [])
    .filter(d => d.role && d.role !== 'owner' && d.wrappedDEK && d._storage !== 'local');
  for (const doc of docs) {
    const secret = Keypair.fromBase64(stored.secretKey);
    let readable = true;
    try {
      (await Keypair.unwrapDEKAnonymous(Keypair.fromBase64(doc.wrappedDEK), secret)).fill(0);
    } catch {
      readable = false;
    } finally { secret.fill(0); }
    if (readable) continue;
    try {
      if (await synchronizeImpl(doc.docId)) {
        markSyncKeyVerified(doc.docId);
        result.restored.push(doc.docId);
      } else {
        result.pending.push(doc.docId); // no authority history yet: owner hasn't restored
      }
    } catch {
      result.pending.push(doc.docId);
    }
  }
  return result;
}

export { fingerprint };
